// FILE: src/components/DenominationCounter.jsx
//
// Shared cash-denomination counting table (Bills / Coins) used by the
// Reconciliation form and the Shift declared-cash counter.
//
// Purely presentational: the PARENT owns the lines array and the running
// total. This component only renders rows and reports quantity edits by
// ORIGINAL array index — so parents with index-based change handlers and
// server-loaded lines (which can arrive in any order or as a subset) stay
// correct. Bills/Coins groups are sorted largest-first for DISPLAY only.
import React from "react";

// Purely numeric display (no currency symbols anywhere in the counter).
const fmt = (n) => (Number(n) || 0).toFixed(2);

// Fresh copy of the standard denomination set (largest note → smallest coin).
export function makeDefaultDenominationLines() {
  return [100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1, 0.05, 0.02, 0.01].map(
    (denomination) => ({ denomination, quantity: 0 })
  );
}

const DENOMC_CSS = `
.denomc { width: 100%; font-size: 13.5px; color: #111827; }
.denomc * { box-sizing: border-box; }
.denomc-headrow, .denomc-row, .denomc-totalrow {
  display: grid;
  grid-template-columns: minmax(100px, 170px) 1fr minmax(120px, 200px);
  gap: 14px; align-items: center;
}
.denomc-headrow {
  padding: 4px 4px 10px;
  border-bottom: 1px solid #DDDDDD;
}
.denomc-headlab {
  font-size: 10.5px; font-weight: 600; letter-spacing: 0.06em;
  text-transform: uppercase; color: #6B7280;
}
.denomc-headlab.is-strong {
  font-size: 12px; font-weight: 700; color: #111827;
}
.denomc-headlab.r { text-align: right; }
.denomc-group {
  padding: 12px 4px 10px;
  font-size: 12px; font-weight: 700; letter-spacing: 0.06em;
  text-transform: uppercase; color: #111827;
  border-bottom: 1px solid #DDDDDD;
}
.denomc-row {
  padding: 7px 4px;
  border-bottom: 1px solid #ECECEC;
}
.denomc-denom {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 14px; font-weight: 600; color: #111827;
}
.denomc-qty input {
  width: 100%; height: 38px; padding: 0 12px;
  border: 1px solid #DDDDDD;
  border-radius: 8px;
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 14px; font-weight: 600;
  color: #111827; background: #fff;
}
.denomc-qty input:disabled { background: #FAFAFA; color: #9CA3AF; }
.denomc-qty input:focus {
  outline: none; border-color: #FFC107;
  box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.22);
}
.denomc-line {
  text-align: right;
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 14px; font-weight: 600; color: #111827;
  padding-right: 4px;
}
.denomc-line.is-zero { color: #6B7280; font-weight: 500; }
.denomc-totalrow { padding: 14px 4px 4px; }
.denomc-total-lab {
  font-size: 12px; font-weight: 700; letter-spacing: 0.06em;
  text-transform: uppercase; color: #111827;
}
.denomc-total-qty {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 15px; font-weight: 700; color: #111827;
  padding-left: 12px;
}
.denomc-total-val {
  justify-self: end;
  background: #FFC107;
  color: #1F1A0A;
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 15px; font-weight: 700;
  padding: 6px 14px;
  border-radius: 8px;
  white-space: nowrap;
}

/* Grid children may shrink — never let a long value force horizontal overflow */
.denomc-headrow > *, .denomc-row > *, .denomc-totalrow > * { min-width: 0; }

/* Input ergonomics: no tiny spinner arrows, no tap delay / double-tap zoom */
.denomc-qty input {
  touch-action: manipulation;
  -moz-appearance: textfield;
  appearance: textfield;
}
.denomc-qty input::-webkit-outer-spin-button,
.denomc-qty input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }

/* ----- Mobile: same layout, thumb-sized inputs, columns re-balanced ----- */
@media (max-width: 600px) {
  .denomc { font-size: 13px; }
  .denomc-headrow, .denomc-row, .denomc-totalrow {
    grid-template-columns: 62px 1fr 86px;
    gap: 8px;
  }
  .denomc-headrow { padding: 2px 2px 8px; }
  .denomc-group { padding: 10px 2px 8px; }
  .denomc-row { padding: 5px 2px; }
  .denomc-denom { font-size: 13.5px; }
  .denomc-qty input {
    height: 44px;        /* comfortable thumb target */
    font-size: 16px;     /* prevents iOS auto-zoom on focus */
    padding: 0 10px;
  }
  .denomc-line { font-size: 13px; padding-right: 0; }
  .denomc-totalrow { padding: 12px 2px 2px; }
  .denomc-total-lab { font-size: 10.5px; letter-spacing: 0.04em; line-height: 1.25; }
  .denomc-total-qty { font-size: 14px; padding-left: 4px; }
  .denomc-total-val { font-size: 14px; padding: 8px 10px; }
}
`;

export default function DenominationCounter({
  lines,
  onQuantityChange,
  // `currency` is accepted for API compatibility but intentionally unused:
  // the counter shows plain numbers, never money symbols.
  currency, // eslint-disable-line no-unused-vars
  disabled = false,
  totalValue = 0,
}) {
  // Preserve original indexes; sort per group for display only.
  const indexed = (lines || []).map((d, idx) => ({ ...d, idx }));
  const bills = indexed
    .filter((d) => d.denomination >= 5)
    .sort((a, b) => b.denomination - a.denomination);
  const coins = indexed
    .filter((d) => d.denomination < 5)
    .sort((a, b) => b.denomination - a.denomination);
  const totalQty = indexed.reduce((s, d) => s + (Number(d.quantity) || 0), 0);

  // Enter (or the mobile keyboard's "Next") jumps to the next quantity input;
  // on the last one it dismisses the keyboard. Purely focus management.
  const handleKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const root = e.currentTarget.closest(".denomc");
    const inputs = root ? Array.from(root.querySelectorAll("input")) : [];
    const next = inputs[inputs.indexOf(e.currentTarget) + 1];
    if (next) next.focus();
    else e.currentTarget.blur();
  };

  const renderRow = (d) => {
    const lineTotal = d.denomination * d.quantity;
    return (
      <div key={d.idx} className="denomc-row">
        <span className="denomc-denom">
          {Number(d.denomination).toFixed(2)}
        </span>
        <div className="denomc-qty">
          <input
            type="number"
            min="0"
            step="1"
            inputMode="numeric"
            autoComplete="off"
            enterKeyHint="next"
            disabled={disabled}
            value={d.quantity}
            onChange={(e) => onQuantityChange(d.idx, e.target.value)}
            onFocus={(e) => e.target.select()}
            onKeyDown={handleKeyDown}
            onWheel={(e) => e.target.blur()}
            aria-label={`Quantity of ${d.denomination}`}
          />
        </div>
        <span className={`denomc-line ${lineTotal ? "" : "is-zero"}`}>
          {fmt(lineTotal)}
        </span>
      </div>
    );
  };

  return (
    <div className="denomc">
      <style>{DENOMC_CSS}</style>
      <div className="denomc-headrow">
        <span className="denomc-headlab is-strong">Bills</span>
        <span className="denomc-headlab">Quantity</span>
        <span className="denomc-headlab r">Line total</span>
      </div>
      {bills.map(renderRow)}
      {coins.length > 0 && <div className="denomc-group">Coins</div>}
      {coins.map(renderRow)}
      <div className="denomc-totalrow">
        <span className="denomc-total-lab">Counted cash total</span>
        <span className="denomc-total-qty">{totalQty}</span>
        <span className="denomc-total-val">
          {fmt(totalValue)}
        </span>
      </div>
    </div>
  );
}
